import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Props {
  page: number;
  pages: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  testIdPrefix?: string;
}

/**
 * Paginazione completa con bottoni numerici, prima/ultima ed ellissi.
 * Mostra "X–Y di N" e gestisce bene anche su mobile (wrap).
 */
export function FullPagination({ page, pages, total, pageSize, onPageChange, testIdPrefix = 'pagination' }: Props) {
  const buttons = useMemo(() => {
    const max = 7;
    if (pages <= max) return Array.from({ length: pages }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(page - 3, pages - max + 1));
    return Array.from({ length: max }, (_, i) => start + i);
  }, [page, pages]);

  if (pages <= 1) return null;

  return (
    <div
      className="flex flex-col sm:flex-row items-center sm:justify-between gap-3 mt-4 sm:mt-5 text-xs sm:text-sm text-muted-foreground"
      data-testid={testIdPrefix}
    >
      <span className="order-2 sm:order-1">
        {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} di {total.toLocaleString('it-IT')}
      </span>
      <div className="flex items-center gap-1 order-1 sm:order-2 flex-wrap justify-center">
        <button
          disabled={page === 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
          className="w-8 h-8 rounded-lg border flex items-center justify-center disabled:opacity-40 hover:bg-secondary/60 transition-colors"
          data-testid={`${testIdPrefix}-prev`}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        {buttons[0] > 1 && (
          <>
            <button
              onClick={() => onPageChange(1)}
              className="w-8 h-8 rounded-lg border text-sm font-medium bg-white hover:bg-secondary/60 transition-colors"
            >1</button>
            {buttons[0] > 2 && <span className="px-1 text-muted-foreground">…</span>}
          </>
        )}
        {buttons.map(p => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={`w-8 h-8 rounded-lg border text-sm font-medium transition-colors ${
              page === p
                ? 'bg-[hsl(214,58%,41%)] text-white border-[hsl(214,58%,41%)]'
                : 'bg-white hover:bg-secondary/60'
            }`}
          >
            {p}
          </button>
        ))}
        {buttons[buttons.length - 1] < pages && (
          <>
            {buttons[buttons.length - 1] < pages - 1 && <span className="px-1 text-muted-foreground">…</span>}
            <button
              onClick={() => onPageChange(pages)}
              className="w-8 h-8 rounded-lg border text-sm font-medium bg-white hover:bg-secondary/60 transition-colors"
            >{pages}</button>
          </>
        )}
        <button
          disabled={page === pages}
          onClick={() => onPageChange(Math.min(pages, page + 1))}
          className="w-8 h-8 rounded-lg border flex items-center justify-center disabled:opacity-40 hover:bg-secondary/60 transition-colors"
          data-testid={`${testIdPrefix}-next`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
